import { FC, useCallback } from "react";

import { selectOptions } from "./../../../../../../../store/selectableOptions/selectors";
import useShallowSelector from "./../../../../../../../hooks/useShalowSelector";
import LogicSelect, { ILogicOptions } from "./../../../../../../../libs/logicSelect";
import { FieldTypes } from "./../../../../../../../store/fields/types";
import { AllLogicTypes, LogicRenderingComponents, LogicSelectValueTypes } from "./../../../LogicTypes";
import { ICondition, IRule } from "./../../../../../../../store/logic/types";
import LogicStateSelect from "./../StateSelect/LogicStateSelect";
import { IconTypes } from "./../../../../../../../libs/Icon";
import NewTooltip from "./../../../../../../../libs/NewTooltip";
import { logicTimeZonesList } from "./../../../../../../../helpers/forTimezones";
import { getSelectValue } from "./../../../../../../../helpers/getSelectValue";
import { LogicAnotherFieldsTypes } from "./../../../LogicTypes";

import styles from "./styles.module.scss";

interface IProps {
    rule: IRule;
    condition: ICondition;
    index: number;
    sortedConditionFields: ILogicOptions;
    setCurrentRule: (newRule: IRule) => void;
}

const ConditionSelect: FC<IProps> = ({ rule, condition, index, sortedConditionFields, setCurrentRule }) => {
    const options = useShallowSelector(selectOptions)[condition._id!];
    const path = `${sortedConditionFields[condition._id!]?.type}.${condition.condition}.${condition.isValue}`;
    const selectValuePath = `${sortedConditionFields[condition._id!]?.type}.${condition.condition}`;

    // const fieldTypes = Object.entries(sortedConditionFields).reduce((acc, current) => {
    //     if (current[0] === condition._id) {
    //         acc = current[1].type;
    //     }
    //     return acc;
    // }, "");
    // const isDate = fieldTypes === FieldTypes.DATE;
    // const isRating = fieldTypes === FieldTypes.STAR_RATING || fieldTypes === FieldTypes.SCALE_RATING;
    // const isChoices =
    //     fieldTypes === FieldTypes.CHECKBOX || fieldTypes === FieldTypes.DROPDOWN || fieldTypes === FieldTypes.RADIO;

    // console.log(path);

    const getOptions = useCallback(
        (fieldType: string) => {
            if (
                (fieldType === FieldTypes.CHECKBOX ||
                    fieldType === FieldTypes.DROPDOWN ||
                    fieldType === FieldTypes.RADIO) &&
                condition.isValue
            ) {
                return Object.keys(options).reduce(
                    (acc, current) => ({
                        ...acc,
                        [options[current].label]: options[current].label,
                    }),
                    {},
                );
            }

            if (
                fieldType === FieldTypes.TIMEZONE &&
                (condition.condition === "IS" || condition.condition === "IS_NOT") &&
                condition.isValue
            )
                return logicTimeZonesList;

            if (
                fieldType === FieldTypes.DATE &&
                (condition.condition === "IS_EQUAL_TO_DAY" || condition.condition === "IS_NOT_EQUAL_TO_DAY")
            ) {
                return {
                    Monday: "Monday",
                    Tuesday: "Tuesday",
                    Wednesday: "Wednesday",
                    Thursday: "Thursday",
                    Friday: "Friday",
                    Saturday: "Saturday",
                    Sunday: "Sunday",
                };
            } else {
                const filteredFields = Object.entries(sortedConditionFields)
                    .filter(field => {
                        if (
                            field[0] !== condition._id &&
                            LogicAnotherFieldsTypes[sortedConditionFields[condition._id!]?.type] &&
                            LogicAnotherFieldsTypes[sortedConditionFields[condition._id!]?.type].includes(
                                sortedConditionFields[field[0]]?.type as FieldTypes,
                            )
                        )
                            return { [sortedConditionFields[field[0]].label]: sortedConditionFields[field[0]].label };

                        return null;
                    })
                    .reduce((acc, current) => {
                        return {
                            ...acc,
                            [current[0]]: current[1].label,
                        };
                    }, {});

                return filteredFields;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sortedConditionFields, condition._id, condition.condition, options],
    );

    const logicContent = (logicContentType: LogicRenderingComponents) => {
        switch (logicContentType) {
            case LogicRenderingComponents.DATEINPUT:
                return (
                    <input
                        placeholder="Type Value"
                        className={styles.valueInput}
                        type="date"
                        value={condition.value}
                        onChange={change => {
                            rule.conditions[index].value = change.target.value;
                            setCurrentRule({ ...rule });
                        }}
                    />
                );

            case LogicRenderingComponents.DROPDOWN:
                return (
                    <LogicSelect
                        placeholder="Select a target field"
                        handleChange={newKey => {
                            rule.conditions[index].target_id = newKey;
                            setCurrentRule({ ...rule });
                        }}
                        options={getOptions(sortedConditionFields[condition._id!]?.type)}
                        valueKey={condition.target_id}
                    />
                );

            case LogicRenderingComponents.TIMEINPUT:
                return (
                    <>
                        <div className={styles.timeWrapper}>
                            <input
                                placeholder="Type Value"
                                className={styles.timetext}
                                value={condition.value}
                                onChange={change => {
                                    rule.conditions[index].value = change.target.value;
                                    setCurrentRule({ ...rule });
                                }}
                            />
                            <NewTooltip
                                usePortal
                                iconType={IconTypes.INFO}
                                content={<span className={styles.tooltipContent}>Set the time like "12:00"</span>}
                            />
                        </div>
                    </>
                );

            case LogicRenderingComponents.TEXTINPUT:
                return (
                    <input
                        placeholder="Type Value"
                        className={styles.valueInput}
                        value={condition.value}
                        onChange={change => {
                            rule.conditions[index].value = change.target.value;
                            setCurrentRule({ ...rule });
                        }}
                    />
                );

            case LogicRenderingComponents.NUMBERINPUT:
                return (
                    <input
                        placeholder="Type Value"
                        className={styles.valueInput}
                        value={condition.value}
                        type="number"
                        min={0}
                        onChange={change => {
                            rule.conditions[index].value = change.target.value;
                            setCurrentRule({ ...rule });
                        }}
                    />
                );

            case LogicRenderingComponents.URLINPUT:
                return (
                    <input
                        placeholder="Type Value"
                        className={styles.valueInput}
                        type="url"
                        value={condition.value}
                        onChange={change => {
                            rule.conditions[index].value = change.target.value;
                            setCurrentRule({ ...rule });
                        }}
                    />
                );

            default:
                return (
                    <LogicSelect
                        placeholder="Select a target field "
                        className={styles.selectTargetField}
                        handleChange={newKey => {
                            rule.conditions[index].target_id = newKey;
                            setCurrentRule({ ...rule });
                        }}
                        disabled={condition.isValue || condition._id === ""}
                        options={sortedConditionFields}
                    />
                );
        }
    };

    if (condition?.condition === "IS_EMPTY" || condition?.condition === "IS_FILLED") return null;

    return (
        <>
            <LogicStateSelect
                handleChange={newKey => {
                    rule.conditions[index].isValue = newKey === "value";
                    setCurrentRule({ ...rule });
                }}
                disabled={condition.condition === undefined || condition.condition === ""}
                options={{
                    value: getSelectValue(LogicSelectValueTypes[selectValuePath]),
                    target: "Another field",
                }}
                valueKey={condition.isValue ? "value" : "target"}
            />

            {logicContent(AllLogicTypes[path])}
        </>
    );
};

export default ConditionSelect;
